import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import PropTypes from 'prop-types';

import { creators as WorkInHandActions } from '../../../Reducers/Reporting/WorkInHand';
import { creators as ReportSettingActions } from '../../../Reducers/Reporting/ReportSetting';
import appdata from '../../../Services/appdata';
import TopChart from './TopChart';
import BottomChart from './BottomChart';

const WorkInHand = ({
  selectedCompany,
  defaultDimDate,
  workInHandSetting,
  bubbleFlag,
}) => {
  const dispatch = useDispatch();
  
  const workInHandSummary = useSelector(state => state.Reporting.Summary.reportingSummaries?.workInHand ?? '');
  const summaryData = useSelector(state => state.Reporting.WorkInHand.summaryData);
  const detailData = useSelector(state => state.Reporting.WorkInHand.detailData);
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );

  const [resize, setResize] = useState(false);
  const [filterName, setFilterName] = useState('Project Type');
  const [selectedTopItems, setSelectedTopItems] = useState([]);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  const getWorkInHandSummary = useCallback((dimDate) => {
    dispatch(WorkInHandActions.workinhandSummaryRequest(dimDate));
  }, [dispatch]);

  const getWorkInHandDetail = useCallback((dimDate) => {
    dispatch(WorkInHandActions.workinhandDetailRequest(dimDate));
  }, [dispatch]);

  const reportSettingUpdateRequest = useCallback((update_setting) => {
    dispatch(ReportSettingActions.reportSettingUpdateRequest(update_setting));
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  useEffect(() => {
    const initializeComponent = async () => {
      if (workInHandSetting) {
        setFilterName(JSON.parse(workInHandSetting).filterName);
      } else {
        await reportSettingUpdateRequest({
          WorkInHand: JSON.stringify({
            filterName: 'Project Type',
          }),
        });
      }

      setSelectedTopItems(
        appdata.getLocalItem('wihSelectedTopItems')
          ? appdata.getLocalItem('wihSelectedTopItems')
          : []
      );
    };

    initializeComponent();
  }, []);

  const triggerWorkInHandRequests = useCallback(() => {
    getWorkInHandSummary(defaultDimDate);
    getWorkInHandDetail(defaultDimDate);
  }, [defaultDimDate, getWorkInHandSummary, getWorkInHandDetail]);

  useEffect(() => {
    if (!lastFetchedCompany || !lastFetchedDimDate) {
      triggerWorkInHandRequests();
    }
  }, [defaultDimDate, lastFetchedCompany, lastFetchedDimDate, triggerWorkInHandRequests]);

  useEffect(() => {
    const valuesChanged =
      lastFetchedCompany !== selectedCompany ||
      lastFetchedDimDate !== defaultDimDate;

    if (
      valuesChanged &&
      lastFetchedCompany &&
      lastFetchedDimDate
    ) {
      triggerWorkInHandRequests();
    }
  }, [defaultDimDate, selectedCompany, lastFetchedCompany, lastFetchedDimDate, triggerWorkInHandRequests]);

  const handleFilter = useCallback(async (event) => {
    if (event.selectedTopItems) {
      appdata.setLocalItem('wihSelectedTopItems', event.selectedTopItems);
      setSelectedTopItems(event.selectedTopItems);
    }

    if (event.filterName) {
      setFilterName(event.filterName);
      await reportSettingUpdateRequest({
        WorkInHand: JSON.stringify({
          filterName: event.filterName,
        }),
      });
    }
  }, [reportSettingUpdateRequest]);
  
  return (
    <div>
      <TopChart
        summaryData={summaryData}
        filterName={filterName}
        selectedTopItems={selectedTopItems}
        detailData={detailData}
        handleFilter={handleFilter}
      />
      <BottomChart
        detailData={detailData}
        filterName={filterName}
        selectedTopItems={selectedTopItems}
        handleFilter={handleFilter}
      />

      {bubbleFlag && workInHandSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed',
            bottom: 60,
            right: 30,
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.5'}
        >
          <Tooltip 
            title={workInHandSummary} 
            position="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

WorkInHand.propTypes = {
  selectedCompany: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  workInHandSetting: PropTypes.string.isRequired,
  bubbleFlag: PropTypes.bool.isRequired,
};

export default WorkInHand;
